const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// The tax generators draw text at fixed x/y points onto the IRS template
// PDFs. Admins tune those points (and import template PDFs for new years)
// from /admin/tax-layouts; this module is where the generators pick the
// tuning up. resolveTaxLayout merges the saved overrides over a generator's
// built-in defaults and swaps the template URL when a year's PDF was
// imported, so a generator only ever asks one question.

let cache = null; // { at, layouts: [{docType, year, fields, hasPdf}] }
const TTL = 60_000;

export async function fetchTaxLayoutIndex() {
  if (cache && Date.now() - cache.at < TTL) return cache.layouts;
  try {
    const res = await fetch(`${BACKEND_URL}/api/tax-layouts`);
    const d = await res.json();
    cache = { at: Date.now(), layouts: Array.isArray(d.layouts) ? d.layouts : [] };
  } catch {
    // offline or the backend is out: the built-in defaults still work
    cache = { at: Date.now() - TTL + 5_000, layouts: cache ? cache.layouts : [] };
  }
  return cache.layouts;
}

export function invalidateTaxLayouts() { cache = null; }

// The admin editor sets this so previews render its unsaved edits live;
// nothing else ever touches it.
let editorOverride = null; // { docType, year, fields }
export function setTaxLayoutEditorOverride(next) { editorOverride = next || null; }

const mergePosition = (base, ovr) => {
  if (Array.isArray(base)) {
    // digits drawn into individual boxes (W-9 SSN/EIN, Schedule C code/EIN):
    // a run override replaces the whole run
    if (Array.isArray(ovr) && ovr.length) return ovr.map((p, i) => ({ ...(base[i] || {}), ...p }));
    // a single-point override saved before the field became a run moves the
    // run so its first box lands there, keeping the spacing
    if (ovr && typeof ovr === "object" && (ovr.x !== undefined || ovr.y !== undefined)) {
      const a = base[0] || {};
      const dx = ovr.x !== undefined ? ovr.x - (a.x || 0) : 0;
      const dy = ovr.y !== undefined ? ovr.y - (a.y || 0) : 0;
      return base.map((p) => ({ ...p, x: (p.x || 0) + dx, y: (p.y || 0) + dy, ...(ovr.fontSize ? { fontSize: ovr.fontSize } : {}) }));
    }
    return base;
  }
  return { ...base, ...(ovr || {}) };
};

export function mergeTaxPositions(defaults, overrides) {
  if (!overrides || typeof overrides !== "object") return defaults;
  const out = { ...defaults };
  for (const key of Object.keys(overrides)) {
    if (key in out) out[key] = mergePosition(out[key], overrides[key]);
  }
  return out;
}

// One call from a generator: the positions to draw with and the template
// URL to fetch, for this form + year.
export async function resolveTaxLayout(docType, taxYear, defaults, defaultUrl) {
  const year = String(parseInt(taxYear, 10) || "");
  if (editorOverride && editorOverride.docType === docType && String(editorOverride.year) === year) {
    return { positions: mergeTaxPositions(defaults, editorOverride.fields), templateUrl: editorOverride.templateUrl || defaultUrl };
  }
  if (!year) return { positions: defaults, templateUrl: defaultUrl };
  const layouts = await fetchTaxLayoutIndex();
  const row = layouts.find((l) => l.docType === docType && String(l.year) === year);
  return {
    positions: row ? mergeTaxPositions(defaults, row.fields) : defaults,
    templateUrl: row && row.hasPdf ? `${BACKEND_URL}/api/tax-templates/${docType}/${year}` : defaultUrl,
  };
}

// The Tax Year dropdowns show the built-in years plus any year an admin
// imported a template PDF for.
export async function taxYearsFor(docType, baseYears) {
  const layouts = await fetchTaxLayoutIndex();
  const imported = layouts.filter((l) => l.docType === docType && l.hasPdf).map((l) => String(l.year));
  return [...new Set([...(baseYears || []), ...imported])].sort((a, b) => Number(b) - Number(a));
}
